"use client";

import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import { useUserContext } from "src/lib/contexts/ContextTypes";
import type IMovieReview from "src/lib/filmwebid/IMovieReview";
import type { IFilmwebIdMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import { getMovieIdentifierQueryParam } from "src/lib/filmwebid/useMovieIdentifier";
import { buildUserApiQueryKey, type UserApiQueryKey, useUserApiQuery, useUserApiQueryFunction } from "src/lib/filmwebid/useUserApiQuery";
export default function useFilmReviewList(movieIdentifier) {
  const $ = _c(21);
  const authContext = useUserContext();
  const fetchWrittenReviews = useUserApiQueryFunction("GET", true, false);
  const numToFetch = useRef(INITIAL_COUNT);
  let commonParams;
  let t0;
  if ($[0] !== movieIdentifier) {
    commonParams = getMovieIdentifierQueryParam(movieIdentifier);
    t0 = buildUserApiQueryKey("/api/Movie/MovieReviews", commonParams);
    $[0] = movieIdentifier;
    $[1] = commonParams;
    $[2] = t0;
  } else {
    commonParams = $[1];
    t0 = $[2];
  }
  const queryKey = t0;
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (lastPage, allPages) => {
      const startIndex = allPages.reduce(_temp, 0);
      if (startIndex < lastPage.totalReviews) {
        return {
          startIndex,
          count: numToFetch.current
        };
      }
    };
    t2 = {
      startIndex: 0,
      count: INITIAL_COUNT
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== fetchWrittenReviews || $[6] !== queryKey) {
    t3 = {
      queryFn: fetchWrittenReviews,
      queryKey,
      getNextPageParam: t1,
      initialPageParam: t2
    };
    $[5] = fetchWrittenReviews;
    $[6] = queryKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: writtenReviews,
    isLoading: isLoadingWrittenReviews,
    fetchNextPage
  } = useInfiniteQuery(t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      method: "GET",
      allowAnon: true
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== authContext.isAuthenticated) {
    t5 = {
      enabled: authContext.isAuthenticated
    };
    $[9] = authContext.isAuthenticated;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: likedReviews,
    isLoading: isLoadingLikedReviews
  } = useUserApiQuery("/api/Reviews/GetLiked", commonParams, t4, t5);
  const isLoading = isLoadingWrittenReviews || isLoadingLikedReviews;
  const t6 = writtenReviews?.pages;
  let t7;
  if ($[11] !== likedReviews || $[12] !== t6) {
    t7 = mergeReviewPages(t6, likedReviews);
    $[11] = likedReviews;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    reviews,
    totalReviews
  } = t7;
  let t8;
  if ($[14] !== fetchNextPage) {
    t8 = count => {
      numToFetch.current = count;
      fetchNextPage();
    };
    $[14] = fetchNextPage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const fetchNext = t8;
  let t9;
  if ($[16] !== fetchNext || $[17] !== isLoading || $[18] !== reviews || $[19] !== totalReviews) {
    t9 = [reviews, totalReviews, isLoading, fetchNext];
    $[16] = fetchNext;
    $[17] = isLoading;
    $[18] = reviews;
    $[19] = totalReviews;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp(acc, page) {
  return acc + (page.reviews?.length ?? 0);
}
const INITIAL_COUNT = 6;

/**
 * Flattens the fetched review pages and marks the reviews whose author is in `likedUserIds` as liked.
 * Returns new review objects: the pages are React Query's cached data, and marking those directly would
 * keep a review "liked" after the user unlikes it or logs out.
 */
export function mergeReviewPages(pages: IMovieReviewsResponse[] | undefined, likedUserIds: string[] | undefined): {
  reviews: IMovieReview[];
  totalReviews: number;
} {
  const reviews = pages?.flatMap(page => page.reviews ?? []).map(review => ({
    ...review,
    isLiked: likedUserIds ? likedUserIds.includes(review.userId) : review.isLiked
  })) ?? [];
  return {
    reviews,
    totalReviews: pages?.[0]?.totalReviews ?? 0
  };
}
export interface IMovieReviewsResponse {
  totalReviews: number;
  reviews: IMovieReview[];
}